import { useCallback, useRef, useSyncExternalStore } from 'react'
import { isEqual as deepEqual } from 'lodash'
import { distinctUntilChanged, map } from 'rxjs'

import { StoreSelector, useEntityLink } from 'src/hooks/useEntity'
import { ItemGroupEntity } from 'src/models/dto/activities/ItemGroupDTO'
import { ItemDTO } from 'src/models/dto/items/ItemDTO'
import { ModuleEntity } from 'src/models/dto/ModuleDTO'
import { ValidationErrorEntity } from 'src/models/dto/TemplateValidationError'
import { ACTIVITY_ENTITY_STORE_SELECTOR } from 'src/services/EntityServices/ActivityEntityService'
import { ITEM_ENTITY_STORE_SELECTOR } from 'src/services/EntityServices/ItemEntityService'
import { MODULE_ENTITY_STORE_SELECTOR } from 'src/services/EntityServices/ModuleEntityService'
import {
    VALIDATION_ERROR_ENTITY_STORE_SELECTOR,
    ValidationErrorEntityService,
} from 'src/services/EntityServices/ValidationErrorEntityService'
import { Store, Stores } from '../services/Store'

export interface UseEntityDeriveProps<T, R, S = T> {
    entityId: string
    selector: StoreSelector<S>
    /** The function must return a stable reference if the entity is unchanged. */
    derive: (entity: T) => R
    equals?: (a: R, b: R) => boolean
}

export function useEntityDerive<T extends S, R, S = T>({
    entityId,
    selector,
    derive,
    equals,
}: UseEntityDeriveProps<T, R, S>) {
    const store = useRef<Store<T>>(Stores.get<T, S>(selector))
    const lastSnapshot = useRef<R | undefined>(undefined)
    const eq = equals ?? deepEqual

    return useSyncExternalStore(
        useCallback(
            (onChange) => {
                const sub = store.current
                    .observeId(entityId)
                    .pipe(
                        map((_evt) => derive(store.current.get(entityId))),
                        distinctUntilChanged(eq)
                    )
                    .subscribe((_derived: R) => {
                        onChange()
                    })
                return () => sub.unsubscribe()
            },
            [entityId, derive, eq]
        ),
        useCallback(() => {
            const next = derive(store.current.get(entityId))
            if (lastSnapshot.current !== undefined && eq(lastSnapshot.current, next)) {
                return lastSnapshot.current
            }
            lastSnapshot.current = next
            return next
        }, [derive, entityId, eq])
    )
}

// TODO add 'equals' parameter to hooks below

export function useItemEntityDerive<R, T extends ItemDTO = ItemDTO>({
    entityId,
    workflowEntityId,
    derive,
}: {
    entityId: string
    workflowEntityId: string
    derive: (item: T) => R
}) {
    useEntityLink({
        entityId: entityId,
        selector: ITEM_ENTITY_STORE_SELECTOR as StoreSelector<T>,
        parent: { entityId: workflowEntityId, selector: ACTIVITY_ENTITY_STORE_SELECTOR },
    })
    return useEntityDerive({
        entityId: entityId,
        selector: ITEM_ENTITY_STORE_SELECTOR as StoreSelector<T>,
        derive,
    })
}

export function useModuleEntityDerive<R>({
    moduleEntityId,
    derive,
}: {
    moduleEntityId: string
    derive: (entity: ModuleEntity) => R
}) {
    return useEntityDerive<ModuleEntity, R>({
        entityId: moduleEntityId,
        selector: MODULE_ENTITY_STORE_SELECTOR,
        derive,
    })
}

export function useItemGroupEntityDerive<R>({
    workflowEntityId,
    moduleEntityId: _ignored,
    derive,
}: {
    workflowEntityId: string
    moduleEntityId: string
    derive: (entity: ItemGroupEntity) => R
}) {
    return useEntityDerive<ItemGroupEntity, R>({
        entityId: workflowEntityId,
        selector: ACTIVITY_ENTITY_STORE_SELECTOR as StoreSelector<ItemGroupEntity>,
        derive,
    })
}

export function useValidationErrorEntityDerive<R>({
    validationErrorEntityId,
    derive,
}: {
    validationErrorEntityId: string
    derive: (value: ValidationErrorEntity) => R
}) {
    if (!ValidationErrorEntityService.has(validationErrorEntityId)) {
        ValidationErrorEntityService.create(validationErrorEntityId)
    }
    return useEntityDerive<ValidationErrorEntity, R>({
        entityId: validationErrorEntityId,
        selector: VALIDATION_ERROR_ENTITY_STORE_SELECTOR,
        derive,
    })
}
